import { fmtMonthDayYear } from '@concord/domain';
import { Button, Crumbs, Notice, PageHead, Panel, PanelHead, PhaseTag, Pill } from '@concord/ui';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { api, errorMessage } from '../../lib/api';
import type { Me } from '../../lib/session';
import { ConsoleLayout } from '../console/ConsoleLayout';
import s from './plans.module.css';

export interface PlanRow {
  id: string;
  name: string;
  status: 'active' | 'upcoming' | 'ended' | 'inactive';
  validFrom: string | null;
  validTo: string | null;
  fundingSource: string | null;
  defaultPool: string | null;
  pools: string[];
  caseTypes: string[];
  defaultPanelSize: number;
  sessionMinutes: number;
  members: number;
  openCases: number;
  totalCases: number;
}
interface PlansList {
  customers: { id: string; name: string; active: boolean; plans: PlanRow[] }[];
}

const STATUS: Record<PlanRow['status'], { tone: 'ok' | 'idle' | 'bad' | 'wait'; label: string }> = {
  active: { tone: 'ok', label: 'Active' },
  upcoming: { tone: 'wait', label: 'Starts later' },
  ended: { tone: 'idle', label: 'Ended' },
  inactive: { tone: 'bad', label: 'Switched off' },
};

/** Spreadsheet-safe CSV: every cell quoted, and a leading = + - @ neutralized. */
export function toCsv(rows: (string | number)[][]): string {
  const cell = (v: string | number) => {
    const t = String(v);
    return `"${(/^[=+\-@]/.test(t) ? `'${t}` : t).replace(/"/g, '""')}"`;
  };
  return rows.map((r) => r.map(cell).join(',')).join('\r\n');
}

/**
 * Customers and their plans, read-only: what each plan covers, who is on it and how many
 * cases it carries. Adding and editing customers and plans is Phase 5.
 */
export function PlansPage({ me }: { me: Me }) {
  const q = useQuery({ queryKey: ['plans-overview'], queryFn: () => api<PlansList>('/staff/plans') });
  const tz = me.timezone;
  const day = (iso: string | null) => (iso ? fmtMonthDayYear(new Date(iso), tz) : null);
  const dates = (p: PlanRow) => {
    const from = day(p.validFrom);
    const to = day(p.validTo);
    if (from && to) return `${from} to ${to}`;
    if (to) return `Until ${to}`;
    if (from) return `From ${from}`;
    return 'Open-ended';
  };

  const download = () => {
    if (!q.data) return;
    const rows: (string | number)[][] = [
      [
        'Customer',
        'Plan',
        'Status',
        'Starts',
        'Ends',
        'Funding source',
        'Networks',
        'Case types covered',
        'Panel size',
        'Consultation minutes',
        'Members now',
        'Open cases',
        'All cases',
      ],
    ];
    for (const c of q.data.customers)
      for (const p of c.plans)
        rows.push([
          c.name,
          p.name,
          STATUS[p.status].label,
          day(p.validFrom) ?? '',
          day(p.validTo) ?? '',
          p.fundingSource ?? '',
          p.pools.join('; '),
          p.caseTypes.length,
          p.defaultPanelSize,
          p.sessionMinutes,
          p.members,
          p.openCases,
          p.totalCases,
        ]);
    const url = URL.createObjectURL(new Blob([toCsv(rows)], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `concord-plans-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <ConsoleLayout me={me} current="plans">
      <Crumbs items={[{ label: 'Customers & plans' }]} />
      <PageHead
        title="Customers & plans"
        when="Every customer's plans: what each covers, who is on it now and how many cases it carries."
        actions={
          <Button variant="sec" disabled={!q.data} onClick={download}>
            Download CSV
          </Button>
        }
      />
      {q.isError && <Notice tone="bad">{errorMessage(q.error)}</Notice>}
      {q.data?.customers.map((c) => (
        <Panel key={c.id} aria-label={c.name} style={{ marginBottom: '1.25rem' }}>
          <PanelHead title={c.name} sub={`${c.plans.length} ${c.plans.length === 1 ? 'plan' : 'plans'}`}>
            {!c.active && <Pill tone="bad">Customer switched off</Pill>}
          </PanelHead>
          <div className="tw">
            <table className={s.table}>
              <thead>
                <tr>
                  <th>Plan</th>
                  <th>Coverage</th>
                  <th>Funding</th>
                  <th>Networks</th>
                  <th>Case types</th>
                  <th className={s.num}>Panel</th>
                  <th className={s.num}>Consult</th>
                  <th className={s.num}>Members</th>
                  <th className={s.num}>Open cases</th>
                  <th className={s.num}>All cases</th>
                </tr>
              </thead>
              <tbody>
                {c.plans.map((p) => (
                  <tr key={p.id}>
                    <td>
                      <strong>{p.name}</strong>
                      <div>
                        <Pill tone={STATUS[p.status].tone}>{STATUS[p.status].label}</Pill>
                      </div>
                    </td>
                    <td>{dates(p)}</td>
                    <td>{p.fundingSource ?? '—'}</td>
                    <td>
                      {p.pools.map((x) => (
                        <div key={x}>
                          {x}
                          {x === p.defaultPool && <span className="sub2"> · default</span>}
                        </div>
                      ))}
                    </td>
                    <td>
                      {p.caseTypes.length ? (
                        <details className={s.types}>
                          <summary>{p.caseTypes.length} covered</summary>
                          <ul>
                            {p.caseTypes.map((t) => (
                              <li key={t}>{t}</li>
                            ))}
                          </ul>
                        </details>
                      ) : (
                        'None'
                      )}
                    </td>
                    <td className={s.num}>{p.defaultPanelSize}</td>
                    <td className={s.num}>{p.sessionMinutes} min</td>
                    <td className={s.num}>
                      <Link
                        to={`/console/directory?role=patient&planId=${p.id}`}
                        title="Open these patients in the directory"
                      >
                        {p.members}
                      </Link>
                    </td>
                    <td className={s.num}>{p.openCases}</td>
                    <td className={s.num}>{p.totalCases}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      ))}
      {q.data && q.data.customers.length === 0 && <Notice>No customers yet.</Notice>}
      <div className={s.note}>
        <PhaseTag phase="P5" />
        Adding and editing customers and plans arrives with administration in Phase 5. Until then the Concord
        team sets them up; send changes to them.
      </div>
    </ConsoleLayout>
  );
}
